import React from 'react';
import { useTranslation } from 'react-i18next';
import './AgentStatusWidget.css';

/**
 * AgentStatusWidget — table "Connected Agents detailed statistics" (KPI agent-status-statistics).
 * Lignes groupées par queue. Fusionne durées de statut (agent_states) et stats d'appels (cdr_hot).
 * Colonnes fixes calquées sur l'exemple ; la map dynamique `statuses` reste dispo dans la donnée
 * pour un détail/tooltip ultérieur.
 */
const AgentStatusWidget = ({ data, config, timezone }) => {
    const { t } = useTranslation();

    if (!Array.isArray(data) || data.length === 0) {
        return null;
    }

    const COLS = 15;

    const fmtInt = (v) => (v === null || v === undefined ? '-' : Number(v).toLocaleString());

    const fmtPct = (v) => {
        if (v === null || v === undefined) return '-';
        const n = Number(v);
        return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)}%`;
    };

    // Durée. < 24h : h:mm:ss (ou m:ss si < 1h). >= 24h : jours + heures + minutes (ex. « 1j 1h26 »)
    // — sur une période de plusieurs mois, « 25:26:10 » se lit mal ; on bascule en jours.
    const fmtDur = (s) => {
        if (s === null || s === undefined) return '-';
        const total = Math.max(0, Math.round(Number(s)));
        const days = Math.floor(total / 86400);
        const h = Math.floor((total % 86400) / 3600);
        const m = Math.floor((total % 3600) / 60);
        const sec = total % 60;
        const pad = (x) => String(x).padStart(2, '0');
        // Au-delà de 24h les secondes sont du bruit → jours + h + min.
        if (days > 0) return `${days}${t('DAY_SHORT')} ${h}h${pad(m)}`;
        return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
    };

    // epoch (secondes) -> heure d'horloge. Pas de date : la période est donnée par le filtre.
    // loginTime/logoutTime = 1er passage à Prêt et fin du dernier, sur la période (l'heure seule ;
    // les secondes masquées peuvent faire paraître un segment de 21s comme 09:16→09:17).
    //
    // `timeZone` = le fuseau choisi dans la barre du rapport. Sans lui, `toLocaleTimeString`
    // suivait le fuseau du POSTE : un agent portugais (UTC+0) connecté à 08:00 s'affichait 09:00
    // chez un superviseur luxembourgeois, et 08:00 chez son collègue de Lisbonne — deux lectures
    // du même rapport, rien à l'écran pour le dire. Repli sur l'heure du poste si le fuseau est
    // absent, ce qui est le comportement d'avant.
    const fmtTime = (epoch) => {
        if (!epoch) return '-';
        try {
            const opts = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
            if (timezone) opts.timeZone = timezone;
            return new Intl.DateTimeFormat('en-GB', opts).format(new Date(Number(epoch) * 1000));
        } catch {
            return '-';
        }
    };

    // Regroupement par queue (la donnée arrive déjà triée queue puis agent).
    const groups = [];
    const idx = {};
    data.forEach((r) => {
        const key = r.queueName || '—';
        if (idx[key] === undefined) {
            idx[key] = groups.length;
            groups.push({ queueName: key, rows: [] });
        }
        groups[idx[key]].rows.push(r);
    });

    return (
        <div className="agent-status-widget">
            <div className="agent-status-table-container">
                <table className="agent-status-table">
                    <thead>
                        <tr>
                            <th className="col-agent">{t('AGENT')}</th>
                            <th>{t('LOGIN')}</th>
                            <th>{t('LOGOUT')}</th>
                            <th>{t('TOTAL_TIME_LOG_ON')}</th>
                            <th>{t('PRESENTED_CALLS')}</th>
                            <th>{t('HANDLED_CALLS')}</th>
                            <th>{t('RING_ON_NO_ANSWER')}</th>
                            <th>{t('ABANDON_CALLS')}</th>
                            <th>{t('TALK_TIME')}</th>
                            <th>{t('MAX_TALK_TIME')}</th>
                            <th>{t('TALK_TIME_PCT')}</th>
                            <th>{t('READY_TIME')}</th>
                            <th>{t('READY_TIME_PCT')}</th>
                            <th>{t('NOT_READY_TIME')}</th>
                            <th>{t('NOT_READY_TIME_PCT')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map((g, gi) => (
                            <React.Fragment key={gi}>
                                <tr className="queue-group">
                                    <td colSpan={COLS}>{g.queueName}</td>
                                </tr>
                                {g.rows.map((row, ri) => (
                                    <tr key={`${gi}-${ri}`}>
                                        <td className="col-agent-val">{row.agentName ?? '-'}</td>
                                        <td className="col-time">{fmtTime(row.loginTime)}</td>
                                        <td className="col-time">{fmtTime(row.logoutTime)}</td>
                                        <td className="col-dur">{fmtDur(row.totalLoggedOn)}</td>
                                        <td className="col-number">{fmtInt(row.presentedCalls)}</td>
                                        <td className="col-number">{fmtInt(row.handledCalls)}</td>
                                        <td className="col-number">{fmtInt(row.ringNoAnswer)}</td>
                                        <td className="col-number">{fmtInt(row.abandonCalls)}</td>
                                        <td className="col-dur">{fmtDur(row.talkTime)}</td>
                                        <td className="col-dur">{fmtDur(row.maxTalkTime)}</td>
                                        <td className="col-pct-val">{fmtPct(row.talkTimePct)}</td>
                                        <td className="col-dur">{fmtDur(row.readyTime)}</td>
                                        <td className="col-pct-val">{fmtPct(row.readyTimePct)}</td>
                                        <td className="col-dur">{fmtDur(row.notReadyTime)}</td>
                                        <td className="col-pct-val">{fmtPct(row.notReadyTimePct)}</td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AgentStatusWidget;
